/**
 * MatchmakingManager
 *
 * Manages core matchmaking message processing and state transitions.
 *
 * Responsibilities:
 * - Process MATCHMAKER_STATE_UPDATE messages
 * - Process MATCHMAKER_HOST_MATCH_FOUND messages
 * - Process MATCHMAKER_ERROR messages
 * - Process MATCHMAKER_CAMPAIGN_SWITCH messages
 * - Update state stores based on messages
 * - Emit coordinator events for state changes
 */

import { MatchmakingMessage, MatchmakingMessageType } from '@playcastdotio/Messaging';
import type { MatchmakingState, InvitationState, CampaignState, SteamCredentialsState } from '../PavilionCoordinator';
import type { CoordinatorEvent } from '../../_types/events';
import type { IHostAnalyticsManager } from '../../../services/analytics/IHostAnalyticsManager';
import * as Sentry from '@sentry/react';

interface MatchmakingStores {
  matchmaking: {
    currentState: MatchmakingState;
    updateState: (state: MatchmakingState) => void;
  };
  invitation: {
    currentState: InvitationState;
    updateState: (state: InvitationState) => void;
  };
  campaign: {
    currentState: CampaignState;
    updateState: (state: CampaignState) => void;
  };
  credentials: {
    currentState: SteamCredentialsState;
    updateState: (state: SteamCredentialsState) => void;
  };
}

interface MatchmakingEvents {
  matchmakingStarted: CoordinatorEvent<void>;
  matchFound: CoordinatorEvent<{ matchId: string }>;
  matchmakingCancelled: CoordinatorEvent<void>;
  matchmakingError: CoordinatorEvent<{ code: string; message: string }>;
  campaignAssigned: CoordinatorEvent<{ campaignId: string }>;
  campaignCleared: CoordinatorEvent<void>;
}

export class MatchmakingManager {
  // Track last processed match ID to prevent duplicate processing
  private lastProcessedMatchId: string | null = null;
  // Track previous matchmaking status for state-change analytics
  private previousMatchmakingStatus: string = 'idle';
  // Track whether a campaign has been received (for isFirstCampaign analytics)
  private hasReceivedCampaign = false;

  constructor(
    private stores: MatchmakingStores,
    private events: MatchmakingEvents,
    private analytics: IHostAnalyticsManager,
  ) {}

  /**
   * Initialize the manager
   */
  init(): void {
    console.log('[MatchmakingManager] Initialized');
  }

  // ========================================
  // Message Handlers
  // ========================================

  /**
   * Handle MATCHMAKER_STATE_UPDATE messages
   */
  handleStateUpdate(message: MatchmakingMessage): void {
    try {
      console.log('[MatchmakingManager] State update:', message);

      if (message.type !== MatchmakingMessageType.MATCHMAKER_STATE_UPDATE) {
        console.error('[MatchmakingManager] Invalid message type:', message.type);
        return;
      }

      const { state, stateDetails } = message;

      switch (state) {
        case 'REGISTERED':
        case 'PREFERENCES_UPDATED':
          this.stores.matchmaking.updateState({
            ...this.stores.matchmaking.currentState,
            status: 'queued',
          });
          this.events.matchmakingStarted.emit();
          this.analytics.sendMatchmakingStateChangedEvent({
            fromStatus: this.previousMatchmakingStatus,
            toStatus: 'queued',
            campaignId: this.stores.campaign.currentState.campaignId,
          });
          this.previousMatchmakingStatus = 'queued';
          break;

        case 'IDLE':
          if (stateDetails?.currentMatchInvitationId) {
            this.stores.matchmaking.updateState({
              ...this.stores.matchmaking.currentState,
              status: 'found',
              matchId: stateDetails.currentMatchInvitationId,
            });
            this.events.matchFound.emit({ matchId: stateDetails.currentMatchInvitationId });
            this.analytics.sendMatchmakingStateChangedEvent({
              fromStatus: this.previousMatchmakingStatus,
              toStatus: 'found',
              campaignId: this.stores.campaign.currentState.campaignId,
            });
            this.previousMatchmakingStatus = 'found';
          } else {
            this.stores.matchmaking.updateState({
              ...this.stores.matchmaking.currentState,
              status: 'idle',
            });
            this.analytics.sendMatchmakingStateChangedEvent({
              fromStatus: this.previousMatchmakingStatus,
              toStatus: 'idle',
              campaignId: this.stores.campaign.currentState.campaignId,
            });
            this.previousMatchmakingStatus = 'idle';
          }
          break;

        case 'MATCHMAKING_DISABLED':
          this.stores.matchmaking.updateState({
            ...this.stores.matchmaking.currentState,
            status: 'cancelled',
          });
          this.events.matchmakingCancelled.emit();
          this.analytics.sendMatchmakingStateChangedEvent({
            fromStatus: this.previousMatchmakingStatus,
            toStatus: 'cancelled',
            campaignId: this.stores.campaign.currentState.campaignId,
          });
          this.previousMatchmakingStatus = 'cancelled';
          break;

        default:
          console.warn('[MatchmakingManager] Unhandled state:', state);
          break;
      }
    } catch (error) {
      console.error('[MatchmakingManager] Error handling state update:', error);
    }
  }

  /**
   * Handle MATCHMAKER_HOST_MATCH_FOUND messages
   */
  handleHostMatchFound(message: MatchmakingMessage): void {
    try {
      console.log('[MatchmakingManager] Host match found:', message);

      if (message.type !== MatchmakingMessageType.MATCHMAKER_HOST_MATCH_FOUND) {
        console.error('[MatchmakingManager] Invalid message type:', message.type);
        return;
      }

      const invitationId = message.invitationId;

      // Deduplicate: Skip if we've already processed this match
      if (this.lastProcessedMatchId === invitationId) {
        console.log('[MatchmakingManager] ⏭️  Skipping duplicate match found:', invitationId);
        return;
      }

      this.lastProcessedMatchId = invitationId;
      const invitation = message.invitation;

      // Update matchmaking state
      this.stores.matchmaking.updateState({
        ...this.stores.matchmaking.currentState,
        status: 'found',
        matchId: invitationId,
      });

      // Update invitation state
      if (invitation) {
        this.stores.invitation.updateState(invitation);
      }

      // Extract and store Steam credentials if provided
      if (message.steamCredentials && message.steamCredentials.length > 0) {
        const steamCreds = message.steamCredentials[0];
        console.log('[MatchmakingManager] Steam credentials received:', {
          username: steamCreds.username,
          hasPassword: !!steamCreds.password,
        });

        this.stores.credentials.updateState({
          username: steamCreds.username,
          password: steamCreds.password,
        });
      }

      // Emit event
      this.events.matchFound.emit({ matchId: invitationId });

      this.analytics.sendHostMatchedEvent({ matchId: invitationId });
    } catch (error) {
      console.error('[MatchmakingManager] Error handling host match found:', error);
    }
  }

  /**
   * Handle MATCHMAKER_ERROR messages
   */
  handleError(message: MatchmakingMessage): void {
    try {
      console.error('[MatchmakingManager] Matchmaker error:', message);

      if (message.type !== MatchmakingMessageType.MATCHMAKER_ERROR) {
        console.error('[MatchmakingManager] Invalid message type:', message.type);
        return;
      }

      const error = message.error;
      if (error) {
        this.events.matchmakingError.emit({
          code: error.code,
          message: error.message,
        });
      }
    } catch (error) {
      console.error('[MatchmakingManager] Error handling error message:', error);
    }
  }

  /**
   * Handle MATCHMAKER_CAMPAIGN_SWITCH messages
   */
  handleCampaignSwitch(message: MatchmakingMessage): void {
    try {
      console.log('[MatchmakingManager] Campaign switch:', message);
      Sentry.logger.info('[MatchmakingManager] Campaign switch:', { message });

      if (message.type !== MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH) {
        console.error('[MatchmakingManager] Invalid message type:', message.type);
        Sentry.logger.error('[MatchmakingManager] Invalid message type:', { type: message.type });
        return;
      }

      const campaignId = message.campaignId;
      const reason = message.reason;

      if (!campaignId || campaignId === '') {
        const previousCampaignId = this.stores.campaign.currentState.campaignId;
        this.stores.campaign.updateState({
          ...this.stores.campaign.currentState,
          campaignId: undefined,
          reason,
        });
        this.events.campaignCleared.emit();
        if (previousCampaignId) {
          this.analytics.sendCampaignClearedEvent({ campaignId: previousCampaignId, reason });
        }
      } else {
        const isFirstCampaign = !this.hasReceivedCampaign;
        this.hasReceivedCampaign = true;
        this.stores.campaign.updateState({
          ...this.stores.campaign.currentState,
          campaignId,
          reason,
        });
        this.events.campaignAssigned.emit({ campaignId });
        this.analytics.sendCampaignReceivedEvent({ campaignId, isFirstCampaign });
      }
    } catch (error) {
      console.error('[MatchmakingManager] Error handling campaign switch:', error);
      Sentry.logger.error('[MatchmakingManager] Error handling campaign switch:', { error });
    }
  }
}
